import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Edit } from "lucide-react";
import { useEffectiveAuth } from "@/hooks/useEffectiveAuth";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";
import { useMembershipAccess } from "@/hooks/useMembershipAccess";
import { useOptimizedDashboardData } from "@/hooks/useOptimizedDashboardData";
import { useImpersonation } from "@/hooks/useImpersonation";
import AccountModal from "@/components/common/AccountModal";
import MessageWidget from "@/components/dashboard/MessageWidget";
import DashboardNotices from "@/components/dashboard/DashboardNotices";
import PendingMentionsAlert from "@/components/dashboard/PendingMentionsAlert";
import ParentAccessRequestsBanner from "@/components/dashboard/ParentAccessRequestsBanner";
import ScoutRequestsAlert from "@/components/dashboard/ScoutRequestsAlert";
import ScoutConnectionAlert from "@/components/dashboard/ScoutConnectionAlert";
import ScoutTaskAssignmentAlert from "@/components/athlete/ScoutTaskAssignmentAlert";
import TaskNotificationBanner from "@/components/athlete/TaskNotificationBanner";

// Lazy load heavy components to improve initial render
import { lazy, Suspense } from "react";

const TasksSummaryBox = lazy(() => import("@/components/dashboard/summary/TasksSummaryBox"));
const CalendarEventsSummaryBox = lazy(() => import("@/components/dashboard/summary/CalendarEventsSummaryBox"));
const SavedCollegesSummaryBox = lazy(() => import("@/components/dashboard/summary/SavedCollegesSummaryBox"));
const ProfileActivitySummaryBox = lazy(() => import("@/components/dashboard/summary/ProfileActivitySummaryBox"));
const ConnectionsSummaryBox = lazy(() => import("@/components/dashboard/summary/ConnectionsSummaryBox"));
const MyTeamsSection = lazy(() => import("@/components/athlete/MyTeamsSection"));
const MyEventsSection = lazy(() => import("@/components/athlete/MyEventsSection"));

// Local type definitions for safe property access
type MinimalProfile = {
  id: string;
  name?: string;
  active_membership_slug?: string;
  [key: string]: any;
};

const AthleteDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useEffectiveAuth();
  const { isImpersonating } = useImpersonation();
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const { hasAccessTo, loading } = useMembershipAccess();
  
  // Removed useTasksSummary hook completely
  
  // Parse URL parameters with extreme logging
  const searchParams = new URLSearchParams(location.search);
  
  // Check if this is parent access with different possible values
  const asParentRaw = searchParams.get('asParent');
  const asScoutRaw = searchParams.get('asScout');
  const isParentAccess = asParentRaw === '1' || asParentRaw === 'true';
  const isScoutAccess = asScoutRaw === '1' || asScoutRaw === 'true';
  
  // Get profile ID for data fetching (could be impersonated or parent access)
  const profileIdFromUrl = searchParams.get('profile_id');
  const targetProfileId = profileIdFromUrl || profile?.id;
  
  // Use optimized data fetching
  const dashboardData = useOptimizedDashboardData(targetProfileId);
  
  // Use the correct profile for display
  const displayProfile = dashboardData.profile || profile;
  
  // Safe access to name property
  const athleteName = displayProfile?.name || 'Athlete';
  
  // Use the premium access hook
  const { hasPremiumAccess } = usePremiumAccess({
    onUpgradeRequired: () => setAccountModalOpen(true),
  });

  // Format membership level for display - use active_membership_slug instead of membership_level
  const formatMembershipLevel = (level: string | undefined | null): { text: string, variant: "membership" | "premium" } => {
    if (!level || level === "Free" || level === "athlete_free") {
      return { text: "Free Athlete", variant: "membership" };
    }

    if (level.toLowerCase().includes("premium") || level === "athlete_premium") {
      return { text: "Premium Athlete", variant: "premium" };
    }

    if (level.toLowerCase().includes("mvp")) {
      return { text: "MVP Athlete", variant: "premium" };
    }

    if (level.toLowerCase().includes("hall_of_fame") || level.toLowerCase().includes("hall of fame")) {
      return { text: "Hall of Fame", variant: "premium" };
    }

    return { text: level.replace(/_/g, " "), variant: "membership" };
  };

  // Use active_membership_slug instead of membership_level
  const membershipInfo = formatMembershipLevel(displayProfile?.active_membership_slug);

  const handleEditProfile = () => {
    if (isParentAccess || isScoutAccess) {
      const editPath = `/athlete-dashboard/profile${location.search}&edit=true`;
      navigate(editPath);
    } else {
      navigate('/athlete-dashboard/profile?edit=true');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Scout Connection Alert */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <ScoutConnectionAlert />}

      {/* Parent Access Requests Banner */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <ParentAccessRequestsBanner />}

      {/* Scout Requests Alert - Only show for regular athletes (not impersonation or parent access) */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <ScoutRequestsAlert />}

      {/* Task Notification Banner - New scout task assignments requiring approval */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <TaskNotificationBanner />}

      {/* Pending Mentions Alert */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <PendingMentionsAlert />}

      {/* Message Widget */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <MessageWidget />}

      {/* Dashboard Notices - Replaces the old admin messages */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && <DashboardNotices />}

      {/* My Teams & Events - Free for all athletes */}
      <div className="grid gap-4 md:grid-cols-2">
        <Suspense fallback={<div className="h-32 dashboard-card animate-pulse" />}>
          <MyTeamsSection />
        </Suspense>
        <Suspense fallback={<div className="h-32 dashboard-card animate-pulse" />}>
          <MyEventsSection />
        </Suspense>
      </div>

      {/* Summary Data - Vertical Stack Layout with Athletic Styling */}
      <div className="flex flex-col gap-y-4 w-full">
        <Link 
          to="/athlete-dashboard/recruiting-tracker" 
          className="block transform transition-all duration-300 hover:-translate-y-1"
          onClick={(e) => {
            if (!hasAccessTo('recruiting-tracker')) {
              e.preventDefault();
              setAccountModalOpen(true);
            }
          }}
        >
          <Suspense fallback={<div className="h-24 dashboard-card animate-pulse" />}>
            <TasksSummaryBox profileId={targetProfileId} onUpgradeClick={() => setAccountModalOpen(true)} />
          </Suspense>
        </Link>

        <Link 
          to="/athlete-dashboard/calendar" 
          className="block transform transition-all duration-300 hover:-translate-y-1"
          onClick={(e) => {
            if (!hasAccessTo('recruiting-calendar')) {
              e.preventDefault();
              setAccountModalOpen(true);
            }
          }}
        >
          <Suspense fallback={<div className="h-24 dashboard-card animate-pulse" />}>
            <CalendarEventsSummaryBox onUpgradeClick={() => setAccountModalOpen(true)} />
          </Suspense>
        </Link>

        <Link 
          to="/athlete-dashboard/college-search" 
          className="block transform transition-all duration-300 hover:-translate-y-1"
          onClick={(e) => {
            if (!hasAccessTo('college-search')) {
              e.preventDefault();
              setAccountModalOpen(true);
            }
          }}
        >
          <Suspense fallback={<div className="h-24 dashboard-card animate-pulse" />}>
            <SavedCollegesSummaryBox onUpgradeClick={() => setAccountModalOpen(true)} />
          </Suspense>
        </Link>

        <Link 
          to="/athlete-dashboard/profile-activity" 
          className="block transform transition-all duration-300 hover:-translate-y-1"
        >
          <Suspense fallback={<div className="h-24 dashboard-card animate-pulse" />}>
            <ProfileActivitySummaryBox 
              hasPremiumAccess={hasPremiumAccess} 
              onUpgradeClick={() => setAccountModalOpen(true)} 
            />
          </Suspense>
        </Link>

        {/* Quick Edit Profile Box */}
        <div 
          className="dashboard-card p-6 cursor-pointer" 
          onClick={handleEditProfile}
        >
          <div className="flex items-start gap-4">
            <div className="dashboard-icon-box-secondary">
              <Edit size={22} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg text-foreground mb-1">Quick Actions</h3>
              <p className="dashboard-label mb-2">Profile Management</p>
              <p className="text-sm text-muted-foreground">Edit your profile and update your information</p>
              <p className="text-xs text-secondary font-bold mt-2">Keep your profile current for coaches →</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Account Modal for upgrades */}
      {!isImpersonating && !isParentAccess && !isScoutAccess && (
        <AccountModal
          open={accountModalOpen}
          onOpenChange={setAccountModalOpen}
          upgradeMessage="Upgrade your account to access premium features and enhance your recruiting journey."
        />
      )}
    </div>
  );
};

export default AthleteDashboard;
